import { create } from 'zustand';
import { buildTemplate, TEMPLATES } from './polyhedra';
import { buildSymmetry, dragWithMode } from './symmetry';

const PANEL_COLORS = ['#111827', '#F9FAFB', '#007AFF', '#FF3B30', '#CCFF00', '#10B981', '#94A3B8', '#7C3AED'];
const RAINBOW_PALETTE = ['#EF4444', '#F59E0B', '#10B981', '#3B82F6', '#7C3AED', '#EC4899', '#14B8A6', '#EAB308'];

// Grouped color palette for the panel-color picker. Each tab exposes a
// curated swatch set so users can quickly find a colour family that fits
// their design — from high-vis vivids to traditional leathers.
const COLOR_GROUPS = {
  Vivid: [
    '#FF3B30', '#FF9500', '#FFCC00', '#CCFF00', '#34C759', '#00C7BE',
    '#007AFF', '#5856D6', '#AF52DE', '#FF2D55', '#EC4899', '#0EA5E9',
  ],
  Pastel: [
    '#FCA5A5', '#FDBA74', '#FDE68A', '#D9F99D', '#BBF7D0', '#A7F3D0',
    '#BAE6FD', '#C7D2FE', '#DDD6FE', '#F5D0FE', '#FBCFE8', '#FEF3C7',
  ],
  Muted: [
    '#111827', '#374151', '#6B7280', '#94A3B8', '#CBD5E1', '#F9FAFB',
    '#7C2D12', '#1E3A8A', '#065F46', '#3F3F46', '#525252', '#A8A29E',
  ],
  Leather: [
    '#8B4513', '#D2B48C', '#954535', '#9F4A1C', '#4A0E0E', '#1F1310',
    '#F5E6D3', '#5C3317', '#A0522D', '#6B4423',
  ],
};

// Greedy proper coloring: adjacent faces always get different palette entries.
// Falls back to cycling through palette when the palette is too small.
function properColoring(faces, palette) {
  const adj = faces.map(() => new Set());
  const edgeMap = new Map();
  faces.forEach((f, fi) => {
    for (let i = 0; i < f.length; i++) {
      const a = f[i];
      const b = f[(i + 1) % f.length];
      const key = a < b ? `${a}_${b}` : `${b}_${a}`;
      if (!edgeMap.has(key)) edgeMap.set(key, []);
      edgeMap.get(key).push(fi);
    }
  });
  for (const fs of edgeMap.values()) {
    if (fs.length === 2) {
      adj[fs[0]].add(fs[1]);
      adj[fs[1]].add(fs[0]);
    }
  }
  const idx = new Array(faces.length).fill(-1);
  for (let i = 0; i < faces.length; i++) {
    const used = new Set();
    for (const j of adj[i]) if (idx[j] >= 0) used.add(idx[j]);
    let picked = -1;
    for (let c = 0; c < palette.length; c++) {
      if (!used.has(c)) {
        picked = c;
        break;
      }
    }
    idx[i] = picked >= 0 ? picked : i % palette.length;
  }
  return idx.map((c) => palette[c]);
}

function pickTwoTone(faces, colorA, colorB) {
  return properColoring(faces, [colorA, colorB]);
}

function defaultColors(faces) {
  return properColoring(faces, RAINBOW_PALETTE);
}

const initial = () => {
  const t = buildTemplate('truncated_icosahedron');
  const sym = buildSymmetry(t);
  return {
    templateId: 'truncated_icosahedron',
    vertices: t.vertices,
    faces: t.faces,
    colors: defaultColors(t.faces),
    rotations: sym.rotations,
    stabilizer: sym.stabilizer,
    stabilizers: sym.stabilizers,
    anchorIdx: sym.anchorIdx,
  };
};

// Clamp a proposed unit-sphere position for vertex `idx` so that no face-edge
// touching it exceeds `maxAngleDeg` on the sphere. Prevents fan-triangulation
// from stretching across neighbouring panels when a handle is dragged too far.
function clampVertexDrag(newPos, vertices, faces, idx, maxAngleDeg = 100) {
  const maxAngle = (maxAngleDeg * Math.PI) / 180;
  const neighbors = new Set();
  for (const f of faces) {
    const pos = f.indexOf(idx);
    if (pos < 0) continue;
    neighbors.add(f[(pos + 1) % f.length]);
    neighbors.add(f[(pos - 1 + f.length) % f.length]);
  }
  let p = [newPos[0], newPos[1], newPos[2]];
  for (let iter = 0; iter < 4; iter++) {
    let violated = false;
    for (const nb of neighbors) {
      const v = vertices[nb];
      const dot = Math.max(-1, Math.min(1, p[0] * v[0] + p[1] * v[1] + p[2] * v[2]));
      const angle = Math.acos(dot);
      if (angle <= maxAngle) continue;
      violated = true;
      // Slerp p back toward v so the angle is exactly maxAngle.
      const t = maxAngle / angle;
      const s = Math.sin(angle);
      const w1 = Math.sin((1 - t) * angle) / s;
      const w2 = Math.sin(t * angle) / s;
      let np = [v[0] * w1 + p[0] * w2, v[1] * w1 + p[1] * w2, v[2] * w1 + p[2] * w2];
      const l = Math.hypot(np[0], np[1], np[2]) || 1;
      p = [np[0] / l, np[1] / l, np[2] / l];
    }
    if (!violated) break;
  }
  return p;
}

// Clamp every edge in the current vertex set so no arc exceeds `maxAngleDeg`.
// Iteratively pulls too-long edges back by slerping both endpoints toward each
// other. Used after `dragWithMode` to make sure symmetric copies of a drag can
// never over-extend.
function clampAllEdges(newV, faces, maxAngleDeg = 100, maxIter = 6) {
  const maxAngle = (maxAngleDeg * Math.PI) / 180;
  const V = newV.length;
  const adj = Array.from({ length: V }, () => new Set());
  for (const f of faces) {
    for (let i = 0; i < f.length; i++) {
      adj[f[i]].add(f[(i + 1) % f.length]);
    }
  }
  let verts = newV.map((v) => [v[0], v[1], v[2]]);
  for (let iter = 0; iter < maxIter; iter++) {
    let anyChange = false;
    for (let i = 0; i < V; i++) {
      for (const j of adj[i]) {
        if (j <= i) continue;
        const va = verts[i];
        const vb = verts[j];
        const dot = Math.max(-1, Math.min(1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]));
        const angle = Math.acos(dot);
        if (angle <= maxAngle) continue;
        anyChange = true;
        // Pull each endpoint half-way toward the target angle.
        const t = 0.5 * (1 - maxAngle / angle);
        const s = Math.sin(angle);
        const wa = Math.sin((1 - t) * angle) / s;
        const wb = Math.sin(t * angle) / s;
        const na = [va[0] * wa + vb[0] * wb, va[1] * wa + vb[1] * wb, va[2] * wa + vb[2] * wb];
        const nb = [vb[0] * wa + va[0] * wb, vb[1] * wa + va[1] * wb, vb[2] * wa + va[2] * wb];
        const la = Math.hypot(na[0], na[1], na[2]) || 1;
        const lb = Math.hypot(nb[0], nb[1], nb[2]) || 1;
        verts[i] = [na[0] / la, na[1] / la, na[2] / la];
        verts[j] = [nb[0] / lb, nb[1] / lb, nb[2] / lb];
      }
    }
    if (!anyChange) break;
  }
  return verts;
}

function normUnit(v) {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
}

export const useStudio = create((set, get) => ({
  ...initial(),
  seamWidth: 0.02,
  finish: 'matte', // matte | gloss | leather
  wireframe: false,
  showHandles: true,
  symmetryMode: 'none', // none | antipodal | mirrorX | mirrorY | mirrorZ | ring | full
  selectedPanel: null,
  panelDragMode: null, // face index being slid, or null
  panelManipMode: 'move', // 'move' | 'scale' | 'rotate' while panelDragMode is active
  smoothVertices: [],  // vertex indices flagged as smooth corners
  isDraggingVertex: false,
  isDraggingPanel: false,
  history: [],
  future: [],

  setTemplate: (id) => {
    const t = buildTemplate(id);
    const sym = buildSymmetry(t);
    set({
      templateId: id,
      vertices: t.vertices,
      faces: t.faces,
      colors: defaultColors(t.faces),
      rotations: sym.rotations,
      stabilizer: sym.stabilizer,
      stabilizers: sym.stabilizers,
      anchorIdx: sym.anchorIdx,
      history: [],
      future: [],
      selectedPanel: null,
      panelDragMode: null,
      smoothVertices: [],
    });
  },

  moveVertex: (idx, newPos) => {
    const { vertices, faces, symmetryMode, rotations, stabilizer, stabilizers, anchorIdx } = get();
    const clamped = clampVertexDrag(newPos, vertices, faces, idx);
    let nv = dragWithMode(symmetryMode, vertices, { rotations, stabilizer, stabilizers, anchorIdx }, idx, clamped);
    // Symmetric copies of a drag can slip past the source clamp, so enforce
    // the same arc limit across every edge in the mesh after the fact.
    nv = clampAllEdges(nv, faces);
    set({ vertices: nv, future: [] });
  },

  // Slide an entire panel across the sphere: rotate every vertex on that
  // face by the quaternion that takes `fromDir` to `toDir`. Shared vertices
  // move with it and neighbouring panels deform naturally so the ball stays
  // interlocked.
  movePanel: (faceIdx, fromDir, toDir) => {
    const { vertices, faces } = get();
    const face = faces[faceIdx];
    if (!face) return;
    const f = normUnit(fromDir);
    const t = normUnit(toDir);
    const dotv = Math.max(-1, Math.min(1, f[0] * t[0] + f[1] * t[1] + f[2] * t[2]));
    if (dotv > 0.99999) return;
    // Rotation axis = f × t
    let ax = [f[1] * t[2] - f[2] * t[1], f[2] * t[0] - f[0] * t[2], f[0] * t[1] - f[1] * t[0]];
    let axLen = Math.hypot(ax[0], ax[1], ax[2]);
    if (axLen < 1e-9) return;
    ax = [ax[0] / axLen, ax[1] / axLen, ax[2] / axLen];
    const angle = Math.acos(dotv);
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const oneMinusC = 1 - c;
    // Rodrigues rotation matrix for axis `ax`, angle `angle`
    const rot = (p) => {
      const [x, y, z] = p;
      const [kx, ky, kz] = ax;
      const dotKP = kx * x + ky * y + kz * z;
      return [
        x * c + (ky * z - kz * y) * s + kx * dotKP * oneMinusC,
        y * c + (kz * x - kx * z) * s + ky * dotKP * oneMinusC,
        z * c + (kx * y - ky * x) * s + kz * dotKP * oneMinusC,
      ];
    };
    const faceSet = new Set(face);
    const nv = vertices.map((v, i) => (faceSet.has(i) ? normUnit(rot(v)) : v));
    const clamped = clampAllEdges(nv, faces);
    set({ vertices: clamped, future: [] });
  },

  // Scale a whole panel — grow/shrink its vertices toward or away from the
  // face centroid along the sphere. `factor > 1` enlarges, `< 1` shrinks.
  scalePanel: (faceIdx, factor) => {
    const { vertices, faces } = get();
    const face = faces[faceIdx];
    if (!face || face.length < 3) return;
    // Face centroid direction.
    const c = [0, 0, 0];
    for (const vi of face) {
      c[0] += vertices[vi][0];
      c[1] += vertices[vi][1];
      c[2] += vertices[vi][2];
    }
    const cn = normUnit(c);
    const faceSet = new Set(face);
    const nv = vertices.map((v, i) => {
      if (!faceSet.has(i)) return v;
      const dp = Math.max(-1, Math.min(1, v[0] * cn[0] + v[1] * cn[1] + v[2] * cn[2]));
      const ang = Math.acos(dp);
      if (ang < 1e-6) return v;
      const tx = v[0] - dp * cn[0], ty = v[1] - dp * cn[1], tz = v[2] - dp * cn[2];
      const tl = Math.hypot(tx, ty, tz) || 1;
      const dir = [tx / tl, ty / tl, tz / tl];
      const newAng = Math.max(0.001, Math.min(Math.PI * 0.75, ang * factor));
      const cA = Math.cos(newAng), sA = Math.sin(newAng);
      return normUnit([
        cA * cn[0] + sA * dir[0],
        cA * cn[1] + sA * dir[1],
        cA * cn[2] + sA * dir[2],
      ]);
    });
    const clamped = clampAllEdges(nv, faces);
    set({ vertices: clamped, future: [] });
  },

  // Rotate a whole panel — spin its vertices around the face centroid axis
  // by `angleRad` radians. Positive = CCW when viewed from outside.
  rotatePanel: (faceIdx, angleRad) => {
    const { vertices, faces } = get();
    const face = faces[faceIdx];
    if (!face || face.length < 3) return;
    const c = [0, 0, 0];
    for (const vi of face) {
      c[0] += vertices[vi][0];
      c[1] += vertices[vi][1];
      c[2] += vertices[vi][2];
    }
    const ax = normUnit(c);
    const cA = Math.cos(angleRad), sA = Math.sin(angleRad), oneMinusC = 1 - cA;
    const rot = (p) => {
      const [x, y, z] = p;
      const [kx, ky, kz] = ax;
      const dotKP = kx * x + ky * y + kz * z;
      return [
        x * cA + (ky * z - kz * y) * sA + kx * dotKP * oneMinusC,
        y * cA + (kz * x - kx * z) * sA + ky * dotKP * oneMinusC,
        z * cA + (kx * y - ky * x) * sA + kz * dotKP * oneMinusC,
      ];
    };
    const faceSet = new Set(face);
    const nv = vertices.map((v, i) => (faceSet.has(i) ? normUnit(rot(v)) : v));
    const clamped = clampAllEdges(nv, faces);
    set({ vertices: clamped, future: [] });
  },

  toggleSmoothVertex: (idx) => {
    const { smoothVertices, symmetryMode, vertices, rotations, stabilizer, stabilizers, anchorIdx } = get();
    // Discover which vertex indices are in the same symmetry orbit as `idx`
    // under the current mode. We probe with a tiny displacement and note
    // every vertex that dragWithMode would move alongside idx.
    const src = vertices[idx];
    const probe = normUnit([src[0] + 0.02, src[1] + 0.017, src[2] + 0.019]);
    const nv = dragWithMode(
      symmetryMode,
      vertices,
      { rotations, stabilizer, stabilizers, anchorIdx },
      idx,
      probe,
    );
    const orbit = new Set([idx]);
    for (let k = 0; k < vertices.length; k++) {
      if (k === idx) continue;
      const a = vertices[k];
      const b = nv[k];
      if (Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]) > 1e-6) orbit.add(k);
    }
    // Toggle collectively: if idx is currently smooth, remove the whole
    // orbit; otherwise add the whole orbit.
    const set2 = new Set(smoothVertices);
    const currentlySmooth = set2.has(idx);
    orbit.forEach((k) => (currentlySmooth ? set2.delete(k) : set2.add(k)));
    set({ smoothVertices: Array.from(set2).sort((a, b) => a - b) });
  },

  setPanelDragMode: (i) => set({ panelDragMode: i, panelManipMode: 'move' }),
  setPanelManipMode: (m) => set({ panelManipMode: m }),
  setDraggingPanel: (v) => set({ isDraggingPanel: v }),

  pushHistory: () => {
    const { history, vertices } = get();
    // clone
    const snap = vertices.map((v) => [v[0], v[1], v[2]]);
    set({ history: [...history, snap].slice(-50), future: [] });
  },

  undo: () => {
    const { history, future, vertices } = get();
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    const snap = vertices.map((v) => [v[0], v[1], v[2]]);
    set({ vertices: prev, history: history.slice(0, -1), future: [...future, snap] });
  },

  redo: () => {
    const { history, future, vertices } = get();
    if (future.length === 0) return;
    const next = future[future.length - 1];
    const snap = vertices.map((v) => [v[0], v[1], v[2]]);
    set({ vertices: next, future: future.slice(0, -1), history: [...history, snap] });
  },

  setPanelColor: (panelIdx, color) => {
    const { colors } = get();
    const nc = colors.slice();
    nc[panelIdx] = color;
    set({ colors: nc });
  },

  setAllColors: (colorA, colorB) => {
    const { faces } = get();
    set({ colors: pickTwoTone(faces, colorA, colorB) });
  },

  setRainbow: () => {
    const { faces } = get();
    set({ colors: properColoring(faces, RAINBOW_PALETTE) });
  },

  setSeamWidth: (w) => set({ seamWidth: w }),
  setFinish: (f) => set({ finish: f }),
  setWireframe: (v) => set({ wireframe: v }),
  setShowHandles: (v) => set({ showHandles: v }),
  setSymmetryLocked: (v) => set({ symmetryMode: v ? 'full' : 'none' }),
  setSymmetryMode: (m) => set({ symmetryMode: m }),
  setDraggingVertex: (v) => set({ isDraggingVertex: v }),
  setSelectedPanel: (i) => set({ selectedPanel: i }),

  loadDesign: (d) => {
    const t = { vertices: d.vertices, faces: d.faces };
    const sym = buildSymmetry(t);
    set({
      templateId: d.template,
      vertices: d.vertices,
      faces: d.faces,
      colors: d.colors,
      rotations: sym.rotations,
      stabilizer: sym.stabilizer,
      stabilizers: sym.stabilizers,
      anchorIdx: sym.anchorIdx,
      seamWidth: d.seam_width ?? 0.02,
      finish: d.finish ?? 'matte',
      smoothVertices: Array.isArray(d.smooth_vertices) ? d.smooth_vertices : [],
      history: [],
      future: [],
      panelDragMode: null,
    });
  },

  reset: () => set({ ...initial(), seamWidth: 0.02, finish: 'matte', history: [], future: [], smoothVertices: [], panelDragMode: null }),
}));

export { PANEL_COLORS, COLOR_GROUPS, TEMPLATES };

// Expose the store for testability (e2e / debugging).
if (typeof window !== 'undefined') {
  window.__studio = {
    get: () => useStudio.getState(),
    moveVertex: (idx, pos) => useStudio.getState().moveVertex(idx, pos),
    setTemplate: (id) => useStudio.getState().setTemplate(id),
    setSymmetryMode: (m) => useStudio.getState().setSymmetryMode(m),
  };
}
